import { SENHA_TAMANHO_MAXIMO, SENHA_TAMANHO_MINIMO } from '@locaveri/shared'

/**
 * Medidor de força de senha.
 *
 * ---------------------------------------------------------------------------
 * O QUE ELE É, E O QUE ELE NÃO É
 * ---------------------------------------------------------------------------
 * É um indicador de PROGRESSO para o usuário, calculado no navegador e sem
 * enviar a senha para lugar nenhum. Não é regra: quem aceita ou recusa a senha
 * é o `senhaSchema` do `@locaveri/shared`, que segue o NIST SP 800-63B —
 * comprimento mínimo de 12, SEM exigência de composição.
 *
 * Por isso o medidor recompensa comprimento acima de tudo. Exigir "1 maiúscula,
 * 1 número e 1 símbolo" empurra todo mundo para `Senha@2026`, que é curta,
 * previsível e passa em qualquer régua de composição. Uma frase longa é melhor
 * senha e precisa marcar mais pontos aqui — senão o medidor ensina o hábito
 * errado.
 */

export type PontuacaoDeSenha = 0 | 1 | 2 | 3 | 4

export interface ForcaDaSenha {
  readonly pontuacao: PontuacaoDeSenha
  readonly rotulo: string
  /** O que fazer para melhorar. Vazio quando já está boa. */
  readonly dica: string
  /** `true` quando a senha já atende ao mínimo que a API aceita. */
  readonly atendeAoMinimo: boolean
}

const ROTULOS: Readonly<Record<PontuacaoDeSenha, string>> = {
  0: 'Muito fraca',
  1: 'Fraca',
  2: 'Razoável',
  3: 'Forte',
  4: 'Excelente',
}

/** Sequências previsíveis de teclado e de contagem. */
const SEQUENCIAS = ['123456789', 'abcdefghij', 'qwertyuiop', 'asdfghjkl', '098765432']

const TEM_MINUSCULA = /[a-z]/
const TEM_MAIUSCULA = /[A-Z]/
const TEM_DIGITO = /\d/
const TEM_SIMBOLO = /[^\p{L}\p{N}]/u

function temSequenciaObvia(senha: string): boolean {
  const minuscula = senha.toLowerCase()

  return SEQUENCIAS.some((sequencia) => {
    for (let inicio = 0; inicio + 4 <= sequencia.length; inicio += 1) {
      if (minuscula.includes(sequencia.slice(inicio, inicio + 4))) {
        return true
      }
    }
    return false
  })
}

function variedade(senha: string): number {
  return [TEM_MINUSCULA, TEM_MAIUSCULA, TEM_DIGITO, TEM_SIMBOLO].filter((padrao) =>
    padrao.test(senha),
  ).length
}

function limitar(valor: number): PontuacaoDeSenha {
  if (valor <= 0) return 0
  if (valor === 1) return 1
  if (valor === 2) return 2
  if (valor === 3) return 3
  return 4
}

export function avaliarForcaDaSenha(senha: string): ForcaDaSenha {
  const atendeAoMinimo =
    senha.length >= SENHA_TAMANHO_MINIMO && senha.length <= SENHA_TAMANHO_MAXIMO

  if (senha.length === 0) {
    return { pontuacao: 0, rotulo: ROTULOS[0], dica: '', atendeAoMinimo: false }
  }

  let pontos = 0

  // Comprimento é o que mais importa (NIST SP 800-63B).
  if (senha.length >= 8) pontos += 1
  if (senha.length >= SENHA_TAMANHO_MINIMO) pontos += 1
  if (senha.length >= 16) pontos += 1
  if (senha.length >= 20) pontos += 1

  // Variedade ajuda, mas não substitui comprimento.
  if (variedade(senha) >= 3) pontos += 1

  // Penalidades: o que um ataque de dicionário resolve em segundos.
  if (new Set(senha).size <= 4) pontos -= 2
  if (temSequenciaObvia(senha)) pontos -= 2

  const pontuacao = atendeAoMinimo ? limitar(pontos) : limitar(Math.min(pontos, 1))

  const dica = !atendeAoMinimo
    ? `Use pelo menos ${String(SENHA_TAMANHO_MINIMO)} caracteres.`
    : pontuacao <= 2
      ? 'Uma frase com quatro palavras é mais segura e mais fácil de lembrar.'
      : ''

  return { pontuacao, rotulo: ROTULOS[pontuacao], dica, atendeAoMinimo }
}
